import * as THREE from "three";
import { VFX } from "../../../../art/vfx.js";
import { PooledVfx } from "./vfxPool";
import type { AnimRig } from "./rig";
import { reducedVfx } from "../ui/settings";
import { loadVfxAtlas } from "./vfxAtlas";

/** A model standing in the hub: the fortress or a tower, its clip rig, and its bounds (world space). */
export interface HubModel {
  readonly root: THREE.Object3D;
  readonly rig: AnimRig;
  readonly box: THREE.Box3;
}

/** A scaffold being raised (an upgrade running) or lowered (it finished, or the builder moved on). */
interface Scaffold { readonly model: HubModel; readonly node: THREE.Object3D; readonly rest: THREE.Vector3; k: number; up: boolean; dust: number }

/**
 * The hub's purposeful motion (the lobby concept, "What moves, and why"): the model a builder is upgrading wears the
 * scaffold its `build` clip carries and puffs a little dust now and then — the timer made visible in the world
 * without a label — and a finished upgrade (or a newly unlocked tower) plays its `build` clip once with a gold burst.
 *
 * The scaffold is the model's own `…scaffold` node (hidden at rest by a near-zero height, raised by the `build`
 * clip), so it fits every tower and fortress tier with no new art: here it is raised and held by driving that node's
 * scale directly, after the clips have posed the model. The sprite effects (art/vfx.js) load on first use only; under
 * Reduced effects there are none, and the clips carry the moment on their own.
 */
export class HubFx {
  /** Seconds the scaffold takes to rise or fold away. */
  static readonly ScaffoldSeconds = 0.6;
  /** Seconds between dust puffs at the foot of a building under upgrade. */
  static readonly DustEvery = 3;
  /** Seconds the celebration's sparks outlive the `build` clip. */
  static readonly CelebrateTail = 1.2;
  /** Dust colour: the trodden-earth brown of the level's placement dust. */
  static readonly DustColour: [number, number, number] = [0.72, 0.62, 0.5];

  private vfx: VFX | null = null;
  private atlas: THREE.Texture | null = null;
  private loading: Promise<void> | null = null;
  private disposed = false;
  private readonly scaffolds: Scaffold[] = [];

  constructor(private readonly scene: THREE.Scene, private readonly camera: THREE.Camera) {}

  /** Raises the scaffold on `model` (lowering any other); null lowers them all. */
  setUpgrading(model: HubModel | null): void {
    for (const s of this.scaffolds) s.up = s.model === model;
    if (!model || this.scaffolds.some((s) => s.model === model)) return;
    const node = HubFx.scaffoldOf(model.root);
    if (!node) return;
    this.scaffolds.push({ model, node, rest: node.scale.clone(), k: 0, up: true, dust: HubFx.DustEvery * 0.5 });
    this.ensureVfx();
  }

  /**
   * Plays `model`'s `build` clip once with a gold burst; its scaffold (if raised) is handed back to the clip, which
   * folds it away itself. Returns the seconds the moment lasts, for the render scheduler.
   */
  celebrate(model: HubModel): number {
    const i = this.scaffolds.findIndex((s) => s.model === model);
    const held = i >= 0 ? this.scaffolds.splice(i, 1)[0] : null;
    const node = held?.node ?? HubFx.scaffoldOf(model.root);
    const rest = held?.rest ?? node?.scale.clone() ?? null;
    // The clip's bindings put back whatever the scaffold's scale was when they were first made — possibly raised —
    // so once the clip is done the scaffold is set to its rest height explicitly.
    const seconds = model.rig.playOnce("build", false, () => { if (node && rest) node.scale.copy(rest); });
    this.ensureVfx().then(() => this.burst(model));
    return seconds + HubFx.CelebrateTail;
  }

  /** The gold burst alone, at `model` (a pet's upgrade finished: it has no `build` clip). Returns its seconds. */
  sparkle(model: HubModel): number {
    this.ensureVfx().then(() => this.burst(model));
    return HubFx.CelebrateTail * 2;
  }

  /**
   * The sprite VFX for ambient effects (the pets' coats: views/petCoatFx.ts): null until loaded — the first ask starts
   * the load (none under Reduced effects).
   */
  ambient(): VFX | null {
    if (!this.vfx && !this.loading) void this.ensureVfx();
    return this.vfx;
  }

  /** Advances the scaffolds and the sprites; true while a scaffold is still moving (it wants every frame). */
  update(dt: number): boolean {
    let moving = false;
    for (let i = this.scaffolds.length - 1; i >= 0; i--) {
      const s = this.scaffolds[i];
      const k = Math.max(0, Math.min(1, s.k + (s.up ? dt : -dt) / HubFx.ScaffoldSeconds));
      if (k !== s.k) moving = true;
      s.k = k;
      const e = k * k * (3 - 2 * k);
      s.node.scale.set(s.rest.x + (1 - s.rest.x) * e, s.rest.y + (1 - s.rest.y) * e, s.rest.z + (1 - s.rest.z) * e);
      if (!s.up && k === 0) { s.node.scale.copy(s.rest); this.scaffolds.splice(i, 1); continue; }
      if (s.up && k === 1 && (s.dust += dt) >= HubFx.DustEvery) { s.dust = 0; this.puff(s.model); }
    }
    this.vfx?.update(dt);
    return moving;
  }

  dispose(): void {
    this.disposed = true;
    for (const s of this.scaffolds) s.node.scale.copy(s.rest);
    this.scaffolds.length = 0;
    this.vfx?.dispose();
    this.atlas?.dispose();
    this.vfx = null;
  }

  private static scaffoldOf(root: THREE.Object3D): THREE.Object3D | null {
    let hit: THREE.Object3D | null = null;
    root.traverse((o) => { if (!hit && /scaffold$/.test(o.name)) hit = o; });
    return hit;
  }

  private static foot(model: HubModel): THREE.Vector3 { return model.box.getCenter(new THREE.Vector3()).setY(model.box.min.y); }

  private puff(model: HubModel): void {
    const at = HubFx.foot(model);
    this.vfx?.dust(at, 6, { size: 0.35, speed: 0.9, color: HubFx.DustColour });
    this.vfx?.smoke(at.setY(at.y + (model.box.max.y - model.box.min.y) * 0.5), 2, { size: 0.35, life: 1.6, rise: 0.5, spread: 0.25, alpha: 0.5 });
  }

  private burst(model: HubModel): void {
    const foot = HubFx.foot(model);
    this.vfx?.towerUpgrade(foot);
    this.vfx?.tierUp(foot.clone().setY(model.box.max.y), 1.6);
  }

  /** Loads the sprite atlas once, on the first effect that needs it (none under Reduced effects). */
  private ensureVfx(): Promise<void> {
    if (reducedVfx()) return Promise.resolve();
    this.loading ??= (async () => {
      try {
        const { meta, texture: tex } = await loadVfxAtlas();
        if (this.disposed) { tex.dispose(); return; }
        this.atlas = tex;
        this.vfx = new PooledVfx(this.scene, this.camera, tex, meta);
      } catch { /* the clips and the scaffold carry the moment on their own */ }
    })();
    return this.loading;
  }
}
